import { FC, useMemo } from 'react';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import { Accordion } from '@mantine/core';
import { UUID } from '@/types/types';
import { AttributeValue } from '@/api/AttributeValueApi';
import { creatorActions } from '@/store/slices/creatorSlice';
import { SingleAttribute } from '../singleAttribute/SingleAttribute';
import { mapAttributesByRelation } from '@/utils/creatorUtils';

export const CollectionConfiguration: FC = () => {
	const dispatch = useTypedDispatch();

	const {
		attributesWithValues,
		globalSelectedAttributeValues: collectionSelectedAttributeValues,
	} = useTypedSelector((state) => state.creator);

	const handleSelectAttributeValue =
		(attributeId: UUID) => (attributeValue: AttributeValue) => () => {
			const changes = [
				{
					attributeId,
					attributeValue,
				},
			];

			if (attributeValue.relatedValue) {
				const relatedValue = attributesWithValues
					.find((attr) => attr.id === attributeValue.relatedValue?.attributeId)
					?.values.find(
						(val) => val.id === attributeValue.relatedValue?.attributeValueId
					);

				if (relatedValue)
					changes.push({
						attributeId: attributeValue.relatedValue.attributeId,
						attributeValue: relatedValue,
					});
			}

			dispatch(creatorActions.handleCollectionAttributeValuesChange(changes));
		};

	const filteredByRelationAttributes = useMemo(
		() =>
			mapAttributesByRelation(
				attributesWithValues,
				Object.values(collectionSelectedAttributeValues).filter((val) => !!val)
			),
		[attributesWithValues, collectionSelectedAttributeValues]
	);

	return (
		<Accordion>
			{filteredByRelationAttributes.map((attribute) => {
				const selectedAttributeValue =
					collectionSelectedAttributeValues[attribute.id];

				return (
					<SingleAttribute
						key={attribute.id}
						attribute={attribute}
						selectedAttributeValue={selectedAttributeValue}
						handleSelectAttributeValue={handleSelectAttributeValue(
							attribute.id
						)}
					/>
				);
			})}
		</Accordion>
	);
};
